import React from "react";
import { CheckCircle, WarningOctagon, XCircle, Info } from "@phosphor-icons/react";

const CHECK_LABELS = {
    hasTitle: "Title",
    hasDescription: "Description",
    hasValidDuration: "Duration",
    hasPlayableOptions: "Playable options",
    hasScoringRules: "Scoring rules",
    rulesMatchChallengeType: "Rules match type",
    noDuplicateChoices: "No duplicate choices",
    playersAreAvailable: "Players available",
    qualityScoreAboveMinimum: "Quality ≥ 50",
    challengeTypeKnown: "Type is known",
};

export default function ValidationPanel({ validation, onValidate, busy }) {
    if (!validation) {
        return (
            <div className="border border-white/10 bg-[#121212] p-3">
                <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-white/50">
                    <Info size={12} weight="bold" />
                    Validation not run yet
                    <button
                        onClick={onValidate}
                        disabled={busy}
                        className="ml-auto px-2 py-1 border border-white/20 text-white/80 hover:border-fa-yellow hover:text-fa-yellow transition-colors disabled:opacity-50"
                        data-testid="run-validation-btn"
                    >
                        Run validation
                    </button>
                </div>
            </div>
        );
    }
    const { is_valid, errors, warnings, checks } = validation;
    const borderColor = is_valid
        ? "border-fa-green/40"
        : "border-fa-red/40";
    const bgColor = is_valid ? "bg-fa-green/5" : "bg-fa-red/5";
    return (
        <div className={`border ${borderColor} ${bgColor} p-3`} data-testid="validation-panel">
            <div className="flex items-center gap-2 mb-2">
                {is_valid ? (
                    <CheckCircle size={16} weight="fill" className="text-fa-green" />
                ) : (
                    <XCircle size={16} weight="fill" className="text-fa-red" />
                )}
                <span
                    className="font-mono text-[11px] uppercase tracking-widest font-bold"
                    style={{ color: is_valid ? "#9EE8B4" : "#FF2A2A" }}
                    data-testid="validation-status"
                >
                    {is_valid ? "Ready to publish" : "Publish blocked"}
                </span>
                {warnings.length > 0 && (
                    <span className="ml-1 font-mono text-[10px] text-fa-yellow flex items-center gap-1">
                        <WarningOctagon size={10} weight="fill" />
                        {warnings.length} warning{warnings.length === 1 ? "" : "s"}
                    </span>
                )}
                <button
                    onClick={onValidate}
                    disabled={busy}
                    className="ml-auto px-2 py-1 font-mono text-[10px] uppercase border border-white/20 text-white/60 hover:border-fa-yellow hover:text-fa-yellow transition-colors disabled:opacity-50"
                    data-testid="run-validation-btn"
                >
                    Re-run
                </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-1.5 mb-2">
                {Object.entries(checks).map(([name, ok]) => (
                    <div
                        key={name}
                        className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider"
                        style={{ color: ok ? "#9ca3af" : "#FF6B6B" }}
                        data-testid={`check-${name}`}
                    >
                        {ok ? (
                            <CheckCircle size={10} weight="fill" className="text-fa-green shrink-0" />
                        ) : (
                            <XCircle size={10} weight="fill" className="text-fa-red shrink-0" />
                        )}
                        <span className="truncate">{CHECK_LABELS[name] || name}</span>
                    </div>
                ))}
            </div>
            {errors.length > 0 && (
                <ul className="space-y-0.5 mt-2">
                    {errors.map((e) => (
                        <li key={e} className="font-mono text-[10px] text-fa-red">
                            ✗ {e}
                        </li>
                    ))}
                </ul>
            )}
            {warnings.length > 0 && (
                <ul className="space-y-0.5 mt-2">
                    {warnings.map((w) => (
                        <li key={w} className="font-mono text-[10px] text-fa-yellow/80">
                            ⚠ {w}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
